import { createPasswordPolicy, PasswordPolicy } from '@acme/b2b/security';
import { passwordValidatorFactory } from '@acme/b2b/validators';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ThemesData } from '@b2b/common/resolvers';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse, TODO, UIBModalService } from '@b2b/types/core';
import { StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';
import invariant from 'tiny-invariant';

UsersEditController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  '$uibModal',
  'ApiService',
  'PermissionsService',
  'ValidateService',
  'HelpersService',
  'AuthenticationService',
  'NoticeFactory',
  'ThemesHelperService',
  'ThemesData',
];

function UsersEditController(
  $scope: IScope & { usersEdit: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  $uibModal: UIBModalService,
  ApiService: ApiService,
  PermissionsService: PermissionsService,
  ValidateService: TODO,
  HelpersService: TODO,
  authenticationService: AuthenticationService,
  NoticeFactory: NoticeFactory,
  ThemesHelperService: ThemesHelperService,
  ThemesData: { data: ACMEResponse<ThemesData> }
) {
  const ctrl = {
    showErrors: false,
    isSubmitted: false,
    isResetSent: false,
    changePassword: false,

    model: {} as TODO,
    selectedRoles: {},
    selectedDepartment: null as TODO | null,
    currentImage: {},
    departments: [] as TODO[],
    tmp: {} as TODO,
    roles: [] as TODO[],
    groups: [] as TODO[],
    notice: new NoticeFactory(),
    cap: HelpersService.cap,
    newPasswordMessage: '',
    confirmPasswordMessage: '',

    passwordPolicy: null as PasswordPolicy | null,

    init: function () {
      $scope.$on('upload::done', ctrl.onUploadDone);
      $scope.$on('upload::add', ctrl.onUploadAdd);
      $scope.$on('upload::error', ctrl.onUploadError);

      if ($state.params && $state.params.id) {
        ctrl.initData();
      }

      if (ThemesData && ThemesData.data.success) {
        ThemesHelperService.setModel(ThemesData.data.payload);

        ctrl.passwordPolicy = createPasswordPolicy({
          passwordSecurityPolicyEnabled: ThemesData.data.payload.company.passwordSecurityPolicyEnabled,
          passwordMinimumLength: ThemesData.data.payload.company.passwordMinimumLength,
          passwordUseMixedCase: ThemesData.data.payload.company.passwordUseMixedCase,
          passwordMinimumDigits: ThemesData.data.payload.company.passwordMinimumDigits,
          passwordMinimumSymbols: ThemesData.data.payload.company.passwordMinimumSymbols,
        });
      }
    },

    initData: function () {
      ctrl
        .initDepartments()
        .then(ctrl.initUser, ctrl.error.getDepartments)
        .then(ctrl.initGroups, ctrl.error.getUser)
        .then(ctrl.success.getGroups, ctrl.error.getGroups);
    },

    initDepartments: function () {
      return ApiService.departments.list({
        page: 1,
        pageSize: -1,
        sortDirection: 'asc',
        sortField: 'name',
      });
    },

    initUser: function (response) {
      ctrl.success.getDepartments(response);
      return ApiService.user.view($state.params.id);
    },

    initGroups: function (response) {
      ctrl.success.getUser(response);
      return ApiService.user.groups.list($state.params.id);
    },

    initRoles: function (response) {
      return ApiService.userProxy.permissions.list(HelpersService.getPaginator());
    },

    getDepartments: function () {
      ApiService.departments
        .list(HelpersService.getPaginator())
        .then((response) => ctrl.success.getDepartments(response))
        .catch((response) => ctrl.error.getDepartments(response));
    },

    getRoles: function () {
      ApiService.userProxy.permissions
        .list(HelpersService.getPaginator())
        .then((response) => ctrl.success.getRoles(response))
        .catch((response) => ctrl.error.getRoles(response));
    },

    getUser: function (id) {
      ApiService.user
        .view(id)
        .then((response) => ctrl.success.getUser(response))
        .catch((response) => ctrl.success.getUser(response));
    },

    getRole: function (id) {
      let r = false;

      for (let i = 0, ii = ctrl.roles.length; i < ii; i++) {
        if (ctrl.roles[i].id === id) {
          r = ctrl.roles[i];
          break;
        }
      }

      return r;
    },

    setModelRoles: function () {
      // Save roles in case something asplodes
      ctrl.tmp.roles = ctrl.model.roles.slice(0);
      ctrl.model.roles = [];

      for (const role in ctrl.selectedRoles) {
        if (ctrl.selectedRoles.hasOwnProperty(role) && ctrl.selectedRoles[role]) {
          ctrl.model.roles.push(ctrl.getRole(+role.split('-')[1]));
        }
      }
    },

    sendResetEmail: function () {
      ApiService.user
        .recover(ctrl.model.email)
        .then((response) => ctrl.success.sendResetEmail(response))
        .catch((response) => ctrl.error.sendResetEmail(response));
    },

    complete: function () {
      const validators: TODO[] = [
        { name: ctrl.model.firstName },
        { name: ctrl.model.lastName },
        { phone: ctrl.model.phoneNumber },
      ];

      if (ctrl.model.hideActionButton) {
        return;
      }

      ctrl.isSubmitted = false;

      if (ctrl.hasUserName()) {
        validators.push({ len: { value: ctrl.model.userName } });
      }

      if (ctrl.hasEmail()) {
        validators.push({ email: ctrl.model.email });
      }

      if (!ctrl.hasUserName() && !ctrl.hasEmail()) {
        validators.push({ email: ctrl.model.email });
      }

      if (ctrl.changePassword === true) {
        validators.push(ctrl.createNewPasswordValidator());
        validators.push(ctrl.createConfirmPasswordValidator());
      }

      if (ValidateService.validate(validators)) {
        ctrl.notice.hideSuccessMessages();
        ctrl.notice.hideErrorMessages();

        ctrl.model.departmentId = ctrl.selectedDepartment ? ctrl.selectedDepartment.id : null;
        ctrl.setModelRoles();
        ApiService.user
          .update(ctrl.model)
          .then((response) => {
            if (ctrl.changePassword === true) {
              ApiService.user
                .adminReset({ id: ctrl.model.id, password: ctrl.model.confirmPassword })
                .then((response) => ctrl.success.complete(response))
                .catch((response) => ctrl.error.complete(response));
            } else {
              ctrl.success.complete(response);
            }
          })
          .catch((response) => ctrl.error.complete(response));
      } else {
        ctrl.isSubmitted = true;

        ctrl.notice.showErrorMessages([{ message: 'Please correct the errors below.' }]);
      }
    },

    hasUserName: function () {
      return typeof ctrl.model.userName === 'string' && ctrl.model.userName.length > 0;
    },

    hasEmail: function () {
      return typeof ctrl.model.email === 'string' && ctrl.model.email.length > 0;
    },

    allowChangePassword: function () {
      return ctrl.hasUserName() && !ctrl.hasEmail();
    },

    isValidFirstName: function () {
      return ctrl.isValidName(ctrl.model.firstName);
    },

    isValidLastName: function () {
      return ctrl.isValidName(ctrl.model.lastName);
    },

    isValidPhone: function () {
      if (ctrl.notice.isLoaded) {
        return ValidateService.validate([{ phone: ctrl.model.phoneNumber }]);
      }
      // Don't test undefined values
      return true;
    },

    isValidEmail: function () {
      if (ctrl.notice.isLoaded && !ctrl.hasUserName()) {
        return ValidateService.validate([{ email: ctrl.model.email }]);
      }

      // Don't test undefined values
      return true;
    },

    isValidName: function (value) {
      if (ctrl.notice.isLoaded) {
        return ValidateService.validate([{ name: value }]);
      }

      // Don't test undefined values
      return true;
    },

    createNewPasswordValidator: function () {
      const passwordPolicy = createPasswordPolicy(ctrl.passwordPolicy ?? {});
      const passwordValidator = passwordValidatorFactory(passwordPolicy);

      return {
        field: 'password',
        predicate: {
          test: { predicateFn: () => passwordValidator(ctrl.model.newPassword) == null },
          message: 'Please enter a valid password',
        },
      };
    },

    createConfirmPasswordValidator: function () {
      return {
        field: 'confirmPassword',
        equal: {
          test: () => [ctrl.model.newPassword, ctrl.model.confirmPassword],
          message: 'Passwords must be the same',
        },
      };
    },

    isValidNewPassword: function () {
      let r = true;

      if (ctrl.isSubmitted && ctrl.changePassword) {
        r = ValidateService.validate([ctrl.createNewPasswordValidator()]);
      }

      if (!r) {
        ctrl.newPasswordMessage = ValidateService.errors[0].message;
      }

      return r;
    },

    isValidConfirmPassword: function () {
      let r = true;

      if (ctrl.isSubmitted && ctrl.changePassword) {
        r = ValidateService.validate([ctrl.createConfirmPasswordValidator()]);
      }

      if (!r) {
        ctrl.confirmPasswordMessage = ValidateService.errors[0].message;
      }

      return r;
    },

    getGroupDetails: function (groupId) {
      return PermissionsService.check('USER_GROUPS_VIEW') ? 'application.groups.roles({ id: ' + groupId + ' })' : '.';
    },

    hasGroups: function () {
      return Array.isArray(ctrl.groups) && ctrl.groups.length > 0;
    },

    findGroup: function (id) {
      for (let i = 0, ii = ctrl.groups.length; i < ii; i++) {
        if (ctrl.groups[i].id === id) {
          return {
            group: ctrl.groups[i],
            index: i,
          };
        }
      }

      return undefined;
    },

    getGroupIds: function () {
      return ctrl.groups.map((group) => group.userGroupMapId);
    },

    getDeltas: function (list) {
      let remove = false;
      const r = {
        ids: [] as TODO[],
        list: {
          mappings: angular.copy(list.mappings),
          groups: angular.copy(list.groups),
        },
      };

      // Only return items to be removed from the list
      // Splice out duplicates to only retain unique values
      for (let i = 0, ii = ctrl.groups.length; i < ii; i++) {
        remove = true;
        for (let j = r.list.mappings.length - 1, jj = 0; j >= jj; j--) {
          if (ctrl.groups[i].id === r.list.mappings[j].userGroupId) {
            r.list.mappings.splice(j, 1);
            r.list.groups.splice(j, 1);
            remove = false;
            break;
          }
        }
        if (remove && ctrl.groups[i].userGroupMapId) {
          r.ids.push(ctrl.groups[i].userGroupMapId);
        }
      }

      return r;
    },

    addBatch: function (list, deltaList) {
      let batchMappings = list.mappings;

      if (deltaList && deltaList.hasOwnProperty('mappings')) {
        batchMappings = deltaList.mappings;
      }

      ApiService.groups.userMaps
        .batchCreate({ mappings: batchMappings })
        .then((response) => {
          const mappings = response.data.payload;

          // Remap userGroupMapIds to model for deletions to work
          for (let i = 0, ii = mappings.length; i < ii; i++) {
            for (let j = 0, jj = list.groups.length; j < jj; j++) {
              if (mappings[i].userGroupId === list.groups[j].id) {
                list.groups[j].userGroupMapId = mappings[i].id;
              }
            }
          }
          ctrl.groups = list.groups;
          ctrl.success.addBatch(response);
        })
        .catch((response) => ctrl.error.addBatch(response));
    },

    addGroup: function () {
      const addModal = $uibModal.open({
        templateUrl: 'views/application/views/users/modals/addGroups.tpl.html',
        size: 'md',
        controller: 'UsersAddGroupsModalController',
        resolve: {
          userData: () => ctrl.model,
          userGroups: () => ctrl.groups,
        },
      });

      addModal.result
        .then((list) => {
          const delta = ctrl.getDeltas(list);

          if (delta.ids.length > 0) {
            ApiService.groups.userMaps
              .remove({ ids: delta.ids })
              .then((response) => {
                // Clear users to prevent duplicates
                ctrl.groups = [];
                // Add users
                ctrl.addBatch(list, delta.list);
              })
              .catch((response) => ctrl.error.addGroup(response));
          } else {
            ctrl.addBatch(list, delta.list);
          }
        })
        .catch(() => {
          // noop
        });
    },

    removeGroup: function (id) {
      const found = ctrl.findGroup(id);
      invariant(found != null);

      const removeModal = $uibModal.open({
        templateUrl: 'views/application/views/users/modals/removeGroup.tpl.html',
        size: 'md',
        controller: 'UsersRemoveGroupModalController',
        resolve: {
          currentGroup: () => found.group,
        },
      });

      removeModal.result
        .then(() => {
          ApiService.groups.userMaps
            .remove({ ids: [found.group.userGroupMapId] })
            .then((response) => {
              ctrl.groups.splice(found.index, 1);
              ctrl.success.remove(response);
            })
            .catch((response) => {
              ctrl.error.remove(response);
            });
        })
        .catch(() => {
          // noop
        });
    },

    isSSO: function () {
      return authenticationService.isSSO();
    },

    success: {
      getUser: function (response) {
        ctrl.model = response.data.user;
        ctrl.model.hideActionButton = !!(ctrl.model.category && ctrl.model.category === 'internal');

        for (let i = 0, ii = ctrl.departments.length; i < ii; i++) {
          if (ctrl.departments[i].id === response.data.user.departmentId) {
            ctrl.selectedDepartment = ctrl.departments[i];
            break;
          }
        }

        if (Array.isArray(response.data.user.roles)) {
          for (let i = 0, ii = ctrl.roles.length; i < ii; i++) {
            for (let j = 0, jj = response.data.user.roles.length; j < jj; j++) {
              if (ctrl.roles[i].id === response.data.user.roles[j].id) {
                ctrl.selectedRoles['role-' + ctrl.roles[i].id] = true;
                break;
              }
            }
          }
        }

        ctrl.notice.hideErrorMessages();
        ctrl.notice.isLoaded = true;

        const buttonRight = !ctrl.model.hideActionButton
          ? {
              label: 'Update',
              permissions: 'USERS_UPDATE',
              action: () => {
                ctrl.complete();
              },
            }
          : null;

        $scope.$emit('Application::contentTitleChange', {
          title: 'Edit User',
          buttonRight,
          buttonLeft: {
            label: '< Back',
            action: () => {
              window.history.go(-1);
            },
          },
        });
      },

      getDepartments: function (response) {
        ctrl.departments = response.data.payload.list;
      },

      getRoles: function (response) {
        ctrl.roles = response.data.payload;

        for (let i = 0, ii = ctrl.roles.length; i < ii; i++) {
          ctrl.selectedRoles['role-' + ctrl.roles[i].id] = false;
        }
      },

      getGroups: function (response) {
        ctrl.groups = response.data.payload;
      },

      sendResetEmail: function (response) {
        ctrl.isResetSent = true;
        ctrl.notice.showSuccessMessages([{ message: 'Password reset email sent!' }], { duration: 3000 });
      },

      complete: function (response) {
        ctrl.showErrors = false;
        ctrl.isSubmitted = true;

        ctrl.notice.showSuccessMessages([{ message: 'Successfully Saved!' }], { duration: 3000 });
      },
      addGroup: function (response) {},
      addBatch: function (response) {},
      remove: function (response) {},
    },

    error: {
      getUser: function (response) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      getDepartments: function (response) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      getRoles: function (response) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      getGroups: function (response) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      sendResetEmail: function (response) {
        ctrl.isResetSent = false;
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      complete: function (response) {
        ctrl.isSubmitted = true;
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },
      addGroup: function (response) {},
      addBatch: function (response) {},
      remove: function (response) {},
    },

    removeProfileImage: function () {
      $scope.$broadcast('upload::delete', ctrl.currentImage);
      ctrl.model.profileImage = null;
      delete ctrl.model.profileImage;
      ctrl.complete();
    },

    onUploadDone: function (e, data) {
      ctrl.notice.hideErrorMessages();
      ctrl.currentImage = data;
      ctrl.model.profileImage = data.file.uuid;
      ctrl.complete();
    },

    onUploadAdd: function (e, data) {
      ctrl.notice.hideErrorMessages();
    },

    onUploadError: function (e, data) {
      if (data && data.result) {
        ctrl.notice.showErrorMessages([{ message: data.result.error || 'There was an error uploading the file.' }]);
      }
    },
  };

  $scope.usersEdit = ctrl;
  ctrl.init();
}

export const UsersEditControllerModule = angular
  .module('acme.view.application.users.edit.controllers', [
    'ui.router',
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.validate',
    'acme.common.service.helpers',
    'acme.common.service.permissions',
    'acme.common.service.themesHelper',
    'acme.common.factory.notice',
    AuthenticationServiceModule,
    'acme.view.application.users.addGroupsModal.controllers',
    'acme.view.application.users.removeGroupModal.controllers',
  ])
  .controller('UsersEditController', UsersEditController).name;
